import React from 'react';
import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import { LenderOffersI } from 'src/interfaces/submissions.interfaces';
import { useTheme } from '@mui/material/styles';

import { hasLendingClubAchDiscount } from './logic/lenderclub-response-table-logic';

const MoreDetailsLink = () => (
  <Link
    href="https://www.lendingclub.com/patientsolutions/app/payment-calculator"
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      color: 'primary.main',
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        color: 'primary.dark',
      },
    }}
  >
    Click here for more details.
  </Link>
);

const DisclosureSection = ({ title, details }: { title: string; details: string[] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
  <Box mb={3}>
    <Typography variant="body2" color="primary" fontWeight="bold">
      {title}
    </Typography>
    {isMobile && (
      <Typography variant="body2" color="primary" sx={{ mb: 2 }}>with LendingClub Bank</Typography>
    )}
    {details.map((detail, index) => (
      <Typography key={index} variant="body2">
        {detail}
      </Typography>
    ))}
    <MoreDetailsLink />
  </Box>
  );
};

const achDiscountDisclaimer =
  '*Get a 0.25% APR discount when you continue the application process on LendingClub, select an offer, link your bank account, and enroll in autopay.';

const disclosureData = [
  {
    id: 'LC_FIXED_RATE_PLAN',
    title: 'Fixed Installment Loan',
    details: [
      '3.99% - 29.99% APR**1',
      'Fixed Rate, Equal Payment Required',
      'Rate based on amount financed and credit history. Payment amount stays the same as loan is paid off.',
    ],
  },
  {
    id: 'LC_TRUE_NO_INTEREST',
    title: 'Fixed Installment Loan',
    details: [
      '0% APR**1',
      'Fixed Rate, Equal Payment Required',
      'Pay no interest for the entire duration of the loan.',
    ],
  },
];

export function LendingClubDisclosuresMobileView({ offers }: { offers: LenderOffersI[] }) {
  const productCode = offers[0]?.product_code;
  if (!productCode) {
    return null;
  }
  const relevantDisclosure = disclosureData.find((disclosure) => disclosure.id === productCode);
  if (!relevantDisclosure) {
    return null;
  }
  return (
    <Box sx={{ width: '100%', padding: { xs: 0, sm: '0 20px' }, backgroundColor: '#fff' }}>
      <DisclosureSection title={relevantDisclosure.title} details={relevantDisclosure.details} />
    </Box>
  );
}
export function LendingClubDisclosuresFooter({ offers = [] }: { offers?: LenderOffersI[] }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hasAchDiscountOffer = offers.some(hasLendingClubAchDiscount);

  return (
    <Box sx={{ width: '100%', padding: { xs: 0, sm: '0 20px' }, mt: 4 }}>
      {!isMobile && (
        disclosureData.map((disclosure) => (
          <DisclosureSection key={disclosure.id} title={disclosure.title} details={disclosure.details} />
        ))
      )}
      {hasAchDiscountOffer && (
        <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
          {achDiscountDisclaimer}
        </Typography>
      )}
      <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
        * Prequalify: A soft inquiry is used to check your eligibility which will not affect your credit score.
      </Typography>
      <Typography variant="body2" color="primary" sx={{ mb: 1 }}>** APR: Annual Percentage Rate</Typography>
      {isMobile && (
      <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
        1 Installment Loans: Installment Loans are issued by LendingClub Bank, N.A., Member FDIC, Equal Housing Lender
        (“LendingClub Bank”). LendingClub Bank offers multiple Installment Loan plans, the availability of which varies
        by provider. Not all plans may be available through your selected provider. Credit terms depend upon credit
        score and other key financing characteristics, including but not limited to the amount financed, loan term
        length, and your credit usage and history.
      </Typography>
      )}
    </Box>
  );
}
